import { FieldValues } from "react-hook-form";

import { CustomParameters } from "./CustomParameters";
import { getIsModelExists, getModels, getProviders } from "./utils";

import { HUGGINGFACE_PROVIDER, VERTEXAI_PROVIDER } from "@/components/ModelParameters/constants";
import { FieldType, StepType, StepT } from "@/components/Stepper/Step/types";
import { Model, Provider } from "@/queries/models";
import { ProviderWithLogo } from "@/components/ProviderWithLogo";

const validateDisplayName = (
  value: string,
  models: Model[],
  isViewing: boolean,
  isEdit: boolean,
) => {
  if (value.length === 0) {
    return "This field is required";
  }

  const modelExists = getIsModelExists(models, value.toLowerCase().trim());
  if (modelExists && !isViewing && !isEdit) {
    return "This model name already exists";
  }

  return true;
};

export const canContinueCustomModelStep1 = ({
  values,
  models,
  isEdit,
  isViewing,
}: {
  values: {
    provider?: string;
    modelName?: string;
    displayName?: string;
    modelType?: string;
    baseModel?: string;
    apiKey?: string;
  };
  models: Model[];
  isEdit: boolean;
  isViewing: boolean;
}): boolean => {
  if (!values.provider || !values.modelName || !values.displayName || !values.modelType) {
    return false;
  }

  if (values.provider === HUGGINGFACE_PROVIDER && !values.apiKey) {
    return false;
  }

  if (values.provider === VERTEXAI_PROVIDER && !values.baseModel) {
    return false;
  }

  return validateDisplayName(values.displayName, models, isViewing, isEdit) === true;
};

export const getCustomModelCreationSteps = ({
  models,
  providers,
  isEdit = false,
  isViewing = false,
  isLoading = false,
  isStep1NextDisabled = false,
}: {
  models: Model[];
  providers: Provider[];
  isEdit?: boolean;
  isViewing?: boolean;
  isLoading?: boolean;
  isStep1NextDisabled?: boolean;
}): StepT[] => [
  {
    id: "create_custom_model",
    title: isEdit ? "Edit Custom Model" : "Create Custom Model",
    description: isEdit
      ? "Edit the custom model to use in your workspace."
      : "Create a custom model to use in your workspace.",
    isOptional: false,
    type: StepType.FIELDS,
    isNextDisabled: isStep1NextDisabled,
    options: [
      {
        label: "Provider",
        id: "provider",
        validation: (value: string) => value.length > 0 || "This field is required",
        readOnly: isViewing,
        type: FieldType.AUTOCOMPLETE,
        fieldOptions: getProviders(providers, isEdit),
        renderOption: option => <ProviderWithLogo provider={option.value} />,
        placeholder: "Select provider",
        addNewOption: false,
      },
      {
        label: fieldsInputs => {
          const { provider } = fieldsInputs;
          return provider === HUGGINGFACE_PROVIDER ? "Model Name or URL" : "Model Name";
        },
        id: "modelName",
        validation: (value: string) => value.length > 0 || "This field is required",
        readOnly: isViewing,
        type: FieldType.AUTOCOMPLETE,
        getAddOptionText: (query: string) => `Add new model: ${query}`,
        fieldOptions: getModels(models),
        tooltip:
          "Select a known model or enter a custom model name. This is the name that will be sent to the LLM provider.",
        addNewOption: true,
        placeholder: fieldsInputs => {
          const { provider } = fieldsInputs;
          return provider === HUGGINGFACE_PROVIDER
            ? "Enter inference client"
            : "Enter custom model name";
        },
      },
      {
        label: "API Key",
        id: "apiKey",
        tooltip: "Enter the API key for HuggingFace model",
        inputType: "password",
        readOnly: isViewing,
        showIf: fieldsInputs => fieldsInputs.provider === HUGGINGFACE_PROVIDER,
        validation: (value: string) => value.length > 0 || "This field is required",
        type: FieldType.TEXT,
        placeholder: "Enter API key",
      },
      {
        label: "Display Name",
        id: "displayName",
        readOnly: isViewing,
        tooltip:
          "This human-readable name is shown when selecting a model in the prompt playground",
        validation: value => validateDisplayName(value, models, isViewing, isEdit),
        type: FieldType.TEXT,
        placeholder: "Enter display name",
      },
      {
        label: "Base Model",
        id: "baseModel",
        readOnly: isViewing,
        showIf: fieldsInputs => fieldsInputs.provider === VERTEXAI_PROVIDER,
        validation: (value: string, fieldsInputs) => {
          if (fieldsInputs?.provider === VERTEXAI_PROVIDER) {
            return value.length > 0 || "This field is required";
          }
          return true;
        },
        type: FieldType.AUTOCOMPLETE,
        fieldOptions: [
          {
            label: "Gemini",
            value: "gemini",
            id: "gemini",
          },
        ],
        tooltip:
          "Select the base model that this fine-tuned model is based on. This helps determine which client SDK to use for parsing.",
        placeholder: "Select base model",
        addNewOption: false,
      },
      {
        label: "Model Type",
        id: "modelType",
        readOnly: isViewing,
        validation: value => value.length > 0 || "This field is required",
        type: FieldType.RADIO,
        fieldOptions: [
          {
            label: "Chat",
            value: "chat",
            id: "chat",
          },
          {
            label: "Completion",
            value: "completion",
            id: "completion",
          },
        ],
        placeholder: "Select model type",
      },
    ],
  },
  {
    id: "parameters",
    title: "Custom Model Parameters",
    type: StepType.CUSTOM_STEP,
    description: "Define the parameters for your custom model.",
    ignoreValidation: true,
    getCustomContent: (formValues: FieldValues) => (
      <CustomParameters isLoading={isLoading} provider={formValues.create_custom_model.provider} />
    ),
    isOptional: false,
  },
];
